import { BUDGETS, ENGAGEMENT_TYPES, TIMINGS } from '@portfolio/api-client';
import { type FormEvent, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { profile } from '../../content/profile.js';
import { Toast } from './Toast.js';
import { TurnstileWidget } from './TurnstileWidget.js';
import { useContactForm } from './useContactForm.js';

export function ContactForm() {
  const { t, i18n } = useTranslation(['form']);
  const {
    values,
    errors,
    submitState,
    setField,
    submit,
    dismissStatus,
    setTurnstileToken,
    turnstileBroken,
    reportTurnstileBroken,
    canSubmit,
  } = useContactForm();

  const ids = {
    name: useId(),
    email: useId(),
    company: useId(),
    problem: useId(),
    when: useId(),
    budget: useId(),
  };

  const sending = submitState.status === 'sending';

  // Stable identities: the widget tears down and re-renders if these change.
  const onToken = useCallback(
    (token: string | null) => setTurnstileToken(token),
    [setTurnstileToken],
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submit();
  };

  return (
    <form className="contact__form" onSubmit={onSubmit} noValidate>
      <h2 className="form__title">{t('form:title')}</h2>
      <p className="form__lede">{t('form:lede')}</p>

      <fieldset className="form__engagement">
        <legend className="visually-hidden">{t('form:engagementLegend')}</legend>
        {/* The design draws these as chips. Real radios sit behind them, so
            arrow-key navigation and announcement come from the platform. */}
        <div className="form__chips">
          {ENGAGEMENT_TYPES.map((option) => (
            <label
              key={option}
              className={`form__chip${values.engagement === option ? ' form__chip--selected' : ''}`}
            >
              <input
                className="visually-hidden"
                type="radio"
                name="engagement"
                value={option}
                checked={values.engagement === option}
                onChange={() => setField('engagement', option)}
              />
              {t(`form:engagement.${option}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="form__row">
        <div>
          <label className="field__label" htmlFor={ids.name}>
            {t('form:name')}
          </label>
          <input
            id={ids.name}
            className={`control${errors.name ? ' control--invalid' : ''}`}
            type="text"
            autoComplete="name"
            placeholder={t('form:namePlaceholder')}
            value={values.name}
            aria-invalid={Boolean(errors.name)}
            onChange={(event) => setField('name', event.target.value)}
          />
          {errors.name && <div className="field__error">{errors.name}</div>}
        </div>
        <div>
          <label className="field__label" htmlFor={ids.email}>
            {t('form:email')}
          </label>
          <input
            id={ids.email}
            className={`control${errors.email ? ' control--invalid' : ''}`}
            type="email"
            dir="ltr"
            autoComplete="email"
            placeholder={t('form:emailPlaceholder')}
            value={values.email}
            aria-invalid={Boolean(errors.email)}
            onChange={(event) => setField('email', event.target.value)}
          />
          {errors.email && <div className="field__error">{errors.email}</div>}
        </div>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={ids.company}>
          {t('form:company')} <span className="field__optional">{t('form:companyOptional')}</span>
        </label>
        <input
          id={ids.company}
          className="control"
          type="text"
          autoComplete="organization"
          placeholder={t('form:companyPlaceholder')}
          value={values.company}
          onChange={(event) => setField('company', event.target.value)}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor={ids.problem}>
          {t('form:problem')}
        </label>
        <textarea
          id={ids.problem}
          className={`control${errors.problem ? ' control--invalid' : ''}`}
          rows={4}
          placeholder={t('form:problemPlaceholder')}
          value={values.problem}
          aria-invalid={Boolean(errors.problem)}
          onChange={(event) => setField('problem', event.target.value)}
        />
        {errors.problem && <div className="field__error">{errors.problem}</div>}
      </div>

      <div className="form__row">
        <div>
          <label className="field__label" htmlFor={ids.when}>
            {t('form:when')}
          </label>
          <select
            id={ids.when}
            className="control"
            value={values.when}
            onChange={(event) => setField('when', event.target.value as (typeof TIMINGS)[number])}
          >
            {TIMINGS.map((option) => (
              <option key={option} value={option}>
                {t(`form:timing.${option}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="field__label" htmlFor={ids.budget}>
            {t('form:budget')} <span className="field__optional">{t('form:budgetOptional')}</span>
          </label>
          <select
            id={ids.budget}
            className="control"
            value={values.budget}
            onChange={(event) => setField('budget', event.target.value as (typeof BUDGETS)[number])}
          >
            {BUDGETS.map((option) => (
              <option key={option} value={option}>
                {t(`form:budgets.${option}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <TurnstileWidget
        locale={i18n.resolvedLanguage ?? 'en'}
        onToken={onToken}
        onError={reportTurnstileBroken}
      />

      {turnstileBroken && <p className="field__error">{t('form:turnstileUnavailable')}</p>}

      <div className="form__submit-row">
        <button type="submit" className="button" disabled={sending || !canSubmit}>
          {sending ? t('form:sending') : t('form:submit')}
        </button>
        <span className="form__reassurance">{t('form:reassurance')}</span>
      </div>

      {submitState.status === 'sent' && (
        <Toast tone="success" onDismiss={dismissStatus} dismissLabel={t('form:dismiss')}>
          <strong className="toast__title">{t('form:status.successTitle')}</strong>
          <p className="toast__text">
            {t('form:status.success', { reference: submitState.reference })}
          </p>
        </Toast>
      )}

      {submitState.status === 'failed' && (
        <Toast tone="error" onDismiss={dismissStatus} dismissLabel={t('form:dismiss')}>
          <strong className="toast__title">{t('form:status.errorTitle')}</strong>
          <p className="toast__text">
            {submitState.reason === 'rate_limited'
              ? t('form:status.rateLimited')
              : submitState.reason === 'verification'
                ? t('form:status.verification')
                : t('form:status.error', { email: profile.email })}
          </p>
        </Toast>
      )}
    </form>
  );
}
